import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCommon from './locales/en/main.json';

const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
    fa: () => import('./locales/fa/main.json'),
    de: () => import('./locales/de/main.json'),
    fr: () => import('./locales/fr/main.json'),
    es: () => import('./locales/es/main.json'),
    'es-ar': () => import('./locales/es-ar/main.json'),
    'pt-br': () => import('./locales/pt-br/main.json'),
    tr: () => import('./locales/tr/main.json'),
    'zh-cn': () => import('./locales/zh-cn/main.json'),
    ja: () => import('./locales/ja/main.json'),
    ko: () => import('./locales/ko/main.json'),
    id: () => import('./locales/id/main.json'),
    sv: () => import('./locales/sv/main.json'),
    no: () => import('./locales/no/main.json'),
    ru: () => import('./locales/ru/main.json'),
    hi: () => import('./locales/hi/main.json'),
};

const supportedAppLocales = new Set(['en', ...Object.keys(localeLoaders)]);

// The profile API historically stores either a language code ("zh") or the
// selector key ("Chinese"). Keep accepting both formats while using the exact
// locale names expected by the website bundles and Next.js routes.
const profileLanguageAliases: Record<string, string> = {
    english: 'en',
    en: 'en',
    persian: 'fa',
    'فارسی': 'fa',
    fa: 'fa',
    german: 'de',
    deutsch: 'de',
    de: 'de',
    french: 'fr',
    'français': 'fr',
    fr: 'fr',
    spanish: 'es',
    'español': 'es',
    es: 'es',
    'spanish (argentina)': 'es-ar',
    'español (argentina)': 'es-ar',
    'es-ar': 'es-ar',
    portuguese: 'pt-br',
    'português': 'pt-br',
    pt: 'pt-br',
    'pt-br': 'pt-br',
    turkish: 'tr',
    'türkçe': 'tr',
    tr: 'tr',
    chinese: 'zh-cn',
    '中文': 'zh-cn',
    zh: 'zh-cn',
    'zh-cn': 'zh-cn',
    japanese: 'ja',
    '日本語': 'ja',
    ja: 'ja',
    korean: 'ko',
    '한국어': 'ko',
    ko: 'ko',
    indonesian: 'id',
    'bahasa indonesia': 'id',
    id: 'id',
    swedish: 'sv',
    svenska: 'sv',
    sv: 'sv',
    norwegian: 'no',
    norsk: 'no',
    no: 'no',
    russian: 'ru',
    'русский': 'ru',
    ru: 'ru',
    hindi: 'hi',
    'हिन्दी': 'hi',
    hi: 'hi',
};

const appLocaleToProfileLanguage: Record<string, string> = {
    en: 'English',
    fa: 'Persian',
    de: 'German',
    fr: 'French',
    es: 'Spanish',
    'es-ar': 'Spanish',
    'pt-br': 'Portuguese',
    tr: 'Turkish',
    'zh-cn': 'Chinese',
    ja: 'Japanese',
    ko: 'Korean',
    id: 'Indonesian',
    sv: 'Swedish',
    no: 'Norwegian',
    ru: 'Russian',
    hi: 'Hindi',
};

/** Resolve profile values to a website locale, or null when the UI has no bundle for it. */
export function resolveSupportedAppLanguage(language: string | null | undefined): string | null {
    if (!language) return null;
    const normalized = language.trim().toLowerCase().replaceAll('_', '-');
    const locale = profileLanguageAliases[normalized] || normalized;
    return supportedAppLocales.has(locale) ? locale : null;
}

/** Return the stable profile value corresponding to a supported website locale. */
export function profileLanguageForAppLocale(language: string | null | undefined): string | null {
    const locale = resolveSupportedAppLanguage(language);
    return locale ? appLocaleToProfileLanguage[locale] || null : null;
}

i18next.use(initReactI18next).init({
    lng: 'en',
    fallbackLng: 'en',
    lowerCaseLng: true,
    interpolation: { escapeValue: false },
    resources: {
        en: { translation: enCommon },
    },
});

// Track full loads ourselves: the landing page adds a partial bundle (landing
// sections only) to the shared store, so hasResourceBundle() is not enough.
const fullyLoaded = new Set<string>(['en']);

/** Load a supported translation bundle without changing the active interface language. */
export async function loadAppLanguage(language: string) {
    const locale = resolveSupportedAppLanguage(language) || 'en';
    const loader = localeLoaders[locale];

    if (loader && !fullyLoaded.has(locale)) {
        const messages = await loader();
        i18next.addResourceBundle(locale, 'translation', messages.default, true, true);
        fullyLoaded.add(locale);
    }

    return loader !== undefined || locale === 'en' ? locale : 'en';
}

export async function changeAppLanguage(language: string) {
    const targetLocale = await loadAppLanguage(language);
    return i18next.changeLanguage(targetLocale);
}

const ONE_YEAR = 60 * 60 * 24 * 365;

/** The language to use: a locale in the URL (/tr, /de …) wins, otherwise the saved choice. */
export function resolveAppLanguage(routeLocale: string | undefined, savedLanguage: string | undefined) {
    if (routeLocale && routeLocale !== 'en') return routeLocale;
    return savedLanguage || 'en';
}

/** Remember the choice for the app (language) and for Next routing (NEXT_LOCALE). */
export function rememberAppLanguage(language: string, routeLocales: readonly string[] = []) {
    if (typeof document === 'undefined') return;
    document.cookie = `language=${language}; path=/; max-age=${ONE_YEAR}; samesite=lax`;
    const routed = routeLocales.includes(language) ? language : 'en';
    document.cookie = `NEXT_LOCALE=${routed}; path=/; max-age=${ONE_YEAR}; samesite=lax`;
}

export default i18next;
